import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { DialogComponent } from '@progress/kendo-angular-dialog';

import { Subscription } from 'rxjs';

import { Communication, FeatureNote } from '@ups/xplat/api/dto';
import { ReleaseNoteService } from '@ups/xplat/core';

import { sanitizeCommunicationHtml } from '../sanitize-communication-html.util';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ups-release-note-popup',
  templateUrl: './release-note-popup.component.html',
  styleUrls: ['./release-note-popup.component.scss'],
  standalone: false,
})
export class ReleaseNotePopupComponent implements OnInit {
  // WI 31712: signals, because the popup is opened from outside (showReleaseNotes$, the
  // manage-communications preview and past-releases-table call showReleasePopup()) and the
  // previous/next navigation lands in an HTTP subscription.
  readonly releaseNote = signal<Communication | undefined>(undefined);
  /** Feature notes grouped by section header, bodies sanitized for [innerHTML]. */
  readonly usedFeatureGroups = computed(() =>
    groupFeatureNotes(this.releaseNote()?.featureNotes).map((group) => ({
      ...group,
      featureNotes: group.featureNotes.map((note) => ({
        ...note,
        safeBody: this.sanitizeHtml(note.body),
      })),
    }))
  );
  readonly hasReleaseDetails = computed(
    () => this.usedFeatureGroups().length > 0
  );
  readonly safeShortDescription = computed(() =>
    this.sanitizeHtml(this.releaseNote()?.shortDescription)
  );
  readonly isNavigating = signal(false);
  readonly showDialog = signal(false);
  /** WI 31661: id on the dialog host so the backdrop handler can scope clicks
   *  to THIS dialog's own `.k-overlay` (the announcements popup opens alongside
   *  it from the same onLogin and renders its own overlay). */
  readonly dialogId = 'releaseNotePopup';
  readonly showDetails = signal(false);
  doLogging = true;
  openedAt = new Date().getTime();
  expandedAt: number = null; // Date().getTime() value
  closeSubscription: Subscription = null;

  @ViewChild('dialog', { static: false }) dialog: DialogComponent;
  @ViewChild('details', { static: false, read: ElementRef })
  detailsContent: ElementRef;
  helpDownloadToken: unknown;
  storedDetailsHeight: number;
  detailsElement: HTMLElement;

  constructor(
    public releaseService: ReleaseNoteService,
    public domSanitizer: DomSanitizer
  ) {
    this.releaseService.showReleaseNotes$.subscribe((x) => {
      this.showReleasePopup(x, true);
    });
  }

  ngOnInit() {}

  /**
   * WI 31640: sanitize an admin-authored communication body for [innerHTML].
   * Replaces the former bypassSecurityTrustHtml, which skipped sanitization and
   * let a stored script/handler execute for every viewer.
   */
  sanitizeHtml(value: string | null | undefined): string {
    return sanitizeCommunicationHtml(this.domSanitizer, value);
  }

  onClose() {
    // Idempotent (WI 31661): four paths can dismiss this modal (the close glyph,
    // the "Got it" button, backdrop click, Escape) and Escape can fire both
    // Kendo's `(close)` and our document listener on one keypress. Guarding on
    // showDialog makes any overlap a no-op instead of a double uploadLogs.
    if (!this.showDialog()) {
      return;
    }
    this.showDialog.set(false);
    if (this.doLogging) {
      const now = new Date().getTime();
      this.releaseService.uploadLogs({
        releaseNoteId: this.releaseNote().communicationId,
        minutesAfterExpansion:
          this.expandedAt == null ? 0 : (now - this.expandedAt) / 60000,
        minutesSpent: (now - this.openedAt) / 60000,
      });
    }
  }

  /**
   * WI 31661: dismiss the release-notes modal on a backdrop (Kendo `.k-overlay`)
   * click. The dialog stays modal (release notes are meant to be seen), but its
   * only exit was a small close glyph, so a user who missed it was trapped
   * behind the full-viewport overlay and every control beneath it (e.g. the
   * standup filter chips) became unclickable.
   *
   * Scoped to THIS dialog's own overlay (a descendant of `#releaseNotePopup`):
   * the announcements popup opens ALONGSIDE release notes from the same
   * ReleaseNoteService.onLogin() response, each rendering its own `.k-overlay`,
   * so a bare `.k-overlay` match would let the announcements backdrop dismiss
   * this dialog and mis-log an unseen release note as read.
   */
  @HostListener('document:click', ['$event'])
  onBackdropClick(event: MouseEvent): void {
    if (!this.showDialog()) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target?.classList?.contains('k-overlay')) {
      return;
    }
    const host = document.getElementById(this.dialogId);
    if (host?.contains(target)) {
      this.onClose();
    }
  }

  /**
   * WI 31661: Escape closes the modal from any focus state. Kendo's own
   * `(close)` only emits when a dialog-wrapper element holds focus (e.g. the
   * auto-focused "Got it" button), so a user who tabbed to the Previous/Next
   * nav buttons could not Escape out. onClose() is idempotent, so the overlap
   * with Kendo's `(close)` on a wrapper-focused Escape is a no-op.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.showDialog()) {
      this.onClose();
    }
  }

  toggleDetails() {
    if (!this.hasReleaseDetails()) {
      return;
    }

    if (this.showDetails()) {
      this.collapseDetails();
    } else {
      this.expandDetails();
    }
  }

  expandDetails() {
    this.showDetails.set(true);
    // this.expandedAt = this.expandedAt || new Date().getTime(); // if expandedAt not null, new date.
    // this.detailsElement.style.setProperty('max-height', this.storedDetailsHeight + 'px');
    // this.detailsElement.style.removeProperty('padding');
  }

  collapseDetails() {
    this.showDetails.set(false);
    // this.storedDetailsHeight = this.detailsElement.scrollHeight;
    // this.detailsElement.style.setProperty('max-height', this.storedDetailsHeight + 'px');
    // setTimeout(() => {
    //   this.detailsElement.style.setProperty('max-height', '0');
    //   this.detailsElement.style.setProperty('padding-top', '0');
    //   this.detailsElement.style.setProperty('padding-bottom', '0');
    // });
  }

  public showReleasePopup(rn: Communication, logging = true) {
    this.releaseNote.set(rn);
    this.showDetails.set(false);
    this.doLogging = logging;
    this.isNavigating.set(false);

    this.releaseService.getDownloadToken().then((x) => {
      this.helpDownloadToken = x;
    });

    this.showDialog.set(true);
    this.openedAt = new Date().getTime();
    this.expandedAt = null; // Prevents cumulating expansions by closing and reopening notes.
  }

  loadPreviousRelease() {
    const releaseNoteId = this.releaseNote()?.previousReleaseNoteId;
    if (!releaseNoteId || this.isNavigating()) {
      return;
    }

    this.navigateToRelease(releaseNoteId);
  }

  loadNextRelease() {
    const releaseNoteId = this.releaseNote()?.nextReleaseNoteId;
    if (!releaseNoteId || this.isNavigating()) {
      return;
    }

    this.navigateToRelease(releaseNoteId);
  }

  private navigateToRelease(releaseNoteId: string) {
    this.isNavigating.set(true);
    this.releaseService.getFullReleaseNote(releaseNoteId).subscribe({
      next: (releaseNote) => {
        this.showReleasePopup(releaseNote, this.doLogging);
      },
      error: () => {
        this.isNavigating.set(false);
      },
    });
  }
}

/** Groups feature notes by section header, preserving first-seen section order. */
function groupFeatureNotes(
  notes: FeatureNote[] | undefined
): { sectionHeader: string; featureNotes: FeatureNote[] }[] {
  const groups = new Map<
    string,
    { sectionHeader: string; featureNotes: FeatureNote[] }
  >();
  for (const note of notes ?? []) {
    let group = groups.get(note.sectionHeader);
    if (!group) {
      group = { sectionHeader: note.sectionHeader, featureNotes: [] };
      groups.set(note.sectionHeader, group);
    }
    group.featureNotes.push(note);
  }
  return Array.from(groups.values());
}
